import { agentControllerRefSchema } from "./agent-controller-ref.js";
import { array, boolean, discriminatedUnion, enum as enumFactory, int, literal, null as nullFactory, number, record, strictObject, string, tuple, union, unknown } from "../../lazy-zod.js";
import { MAX_TEXT_LENGTH } from "../protocol/constants.js";
import { agentProfileIdSchema, profileHandleSchema, profileWorkingDirectorySchema, profileWorkspaceModeSchema } from "../protocol/profile.js";
import { daemonServiceLaneSchema } from "./service-contract.js";
import { localFolderPickerRuntimeCapabilitySchema } from "./local-folder-picker.js";
import { agentProfileWorkRecoveryActionKindSchema } from "../agent-profile-work-recovery.js";
import { executionRecoveryDestinationSchema, executionRecoveryIdentitySchema } from "../http/execution-recovery.js";
import { LOCAL_AGENT_REASONING_MAX_CHARS, localAgentReasoningStateSchema } from "../http/local-agent-reasoning.js";
import { organizationProtectedInformationSchema } from "../http/organization-information-protection.js";
import { spaceMentionLabelSchema } from "../http/space-mention-label.js";
import { spacePasswordSchema } from "../http/space-password.js";
import { runtimeTransportDeliveryExecutionIdentitySchema, runtimeTransportResultIdentityEchoSchema, runtimeTransportRuntimeEvidenceIdentityEchoSchema } from "./execution-identity.js";
import { runtimeCoordinatorExecutionTokenEchoSchema, runtimeCoordinatorExecutionTokenEnvelopeSchema } from "./runtime-coordinator-execution-token.js";
import { runtimeCoordinatorExecutionRecordSchema } from "./runtime-coordinator-execution.js";
import { clientMessageIdSchema } from "../protocol/client-message-id.js";
import { postSpaceWakeStopResponseSchema } from "../http/space-wake-stop.js";
import { localComponentsStatusSchema, openMeldToolsControlCapabilitySchema, openMeldToolsControlRequestSchema, openMeldToolsControlResultSchema } from "../local-components.js";
import { agentInteractionPendingSummarySchema, agentInteractionRequestSchema, agentInteractionResponseSchema } from "../protocol/agent-interaction.js";
import { agentStreamToolPartSchema } from "../protocol/agent-stream.js";
import { executionContextTaskSchema } from "../protocol/execution-context.js";
import { messageEnvelopeSchema } from "../protocol/message-envelope.js";
import { targetWakeLifecycleIdentitySchema } from "../protocol/target-wake-lifecycle.js";
import { workingLanguageGuidanceSchema } from "../runtime/working-language.js";
import { spaceContractSchema } from "../space-mesh/space-contract.js";
import { spaceThreadContextSchema } from "../space-mesh/space-thread.js";
import { executionHostObservationSchema } from "./computer-care.js";
import { runtimeAgentControllerConversationIdMappingSpecSchema, runtimeDispatchPolicySchema, runtimeStreamingModeSchema, runtimeTransportModeSchema } from "./controller-contract.js";
import { openMeldDaemonCancelPayloadSchema, openMeldDaemonCancelRequestPayloadSchema, openMeldDaemonInterruptPayloadSchema, openMeldDaemonInterruptRequestPayloadSchema, openMeldDaemonManageLocalAgentInstallationPayloadSchema, openMeldDaemonManageLocalAgentInstallationRequestPayloadSchema, openMeldDaemonSetLocalAgentEnabledPayloadSchema, openMeldDaemonSetLocalAgentEnabledRequestPayloadSchema, openMeldDaemonSetProfileWorkspacePayloadSchema, openMeldDaemonSetProfileWorkspaceRequestPayloadSchema, openMeldDaemonSyncRouteCatalogPayloadSchema, openMeldDaemonSyncRouteCatalogRequestPayloadSchema, skippedDaemonProfileSchema } from "./control-plane.js";
import { runtimeTransportStopCauseSchema } from "./runtime-transport-stop-cause.js";
import { openMeldDaemonAcceptedDispatchSnapshotSchema, openMeldDaemonDispatchContinuitySnapshotSchema, openMeldDaemonDispatchControllerSnapshotSchema, openMeldDaemonDispatchDeliveryRecoveryReasonSchema, openMeldDaemonDispatchReplyToSnapshotSchema, openMeldDaemonDispatchRulesSnapshotSchema, openMeldDaemonDispatchSourceSignalSnapshotSchema } from "./dispatch-queue.js";
import { daemonRuntimeAdapterCapabilitySnapshotSchema } from "./provider-runtime-capability.js";
import { openMeldDaemonTaskResultEnvelopeSchema } from "./task-result.js";
//#region src/schemas/daemon/runtime-transport.ts
const OPENMELD_EXECUTION_ADMISSION_HEADER = "x-openmeld-execution-admission";
const RUNTIME_TRANSPORT_SCHEMA_VERSION = 1;
const CURRENT_DAEMON_EXECUTION_CONTRACT_EPOCH = "runtime-transport-epoch-2026-09-image-geometry";
const daemonRuntimeContractSchema = strictObject({ epoch: string().min(1) });
const daemonRuntimeContractReasonCodeSchema = enumFactory([
	"daemon.runtime_contract.compatible",
	"daemon.runtime_contract.missing",
	"daemon.runtime_contract.mismatch"
]);
const daemonExecutionCompatibilitySchema = strictObject({
	status: enumFactory(["compatible", "update_required"]),
	expectedEpoch: string().min(1),
	observedEpoch: union([string().min(1), nullFactory()]),
	reasonCode: daemonRuntimeContractReasonCodeSchema
});
const runtimeTransportAgentProfileExecutionStateRequestSchema = strictObject({
	ownerUserId: string().trim().min(1).max(256),
	targetProfileId: agentProfileIdSchema
});
const runtimeTransportAgentProfileExecutionStateResponseSchema = discriminatedUnion("status", [
	strictObject({ status: literal("active") }),
	strictObject({ status: literal("inactive") }),
	strictObject({
		reasonCode: string().trim().min(1).max(256),
		status: literal("unavailable")
	})
]);
const runtimeTransportAgentProfileWorkReferenceSchema = strictObject({
	sourceSignalId: string().trim().min(1).max(512),
	spaceId: string().trim().min(1).max(512)
});
const runtimeTransportAgentProfileWorkRecoveryActionSchema = agentProfileWorkRecoveryActionKindSchema;
const runtimeTransportAgentProfileWorkRecoveryReadRequestSchema = strictObject({
	ownerUserId: string().trim().min(1).max(256),
	targetProfileId: agentProfileIdSchema
});
const runtimeTransportAgentProfileWorkRecoveryReadResponseSchema = discriminatedUnion("status", [
	strictObject({
		action: runtimeTransportAgentProfileWorkRecoveryActionSchema,
		authorizedContinuationClientMessageId: clientMessageIdSchema.nullable(),
		forkInterruptedConversation: boolean().optional(),
		interruptedClientMessageId: clientMessageIdSchema,
		interruptedDispatchId: string().trim().min(1).max(512),
		interruptedTargetWakeLifecycleId: string().trim().min(1).max(512).optional(),
		interruptedSourceSignalId: string().trim().min(1).max(512).optional(),
		interruptedLocalAttempt: enumFactory([
			"awaiting_withdrawal",
			"not_started",
			"withdrawn"
		]).optional(),
		spaceId: string().trim().min(1).max(512),
		status: literal("available")
	}),
	strictObject({ status: literal("not_pending") }),
	strictObject({ status: literal("unavailable") })
]);
const runtimeTransportAgentProfileOperationalContextSchema = discriminatedUnion("status", [
	strictObject({
		reasonCode: string().trim().min(1).max(256),
		status: literal("checking")
	}),
	strictObject({
		status: literal("idle"),
		waitingCount: literal(0)
	}),
	strictObject({
		currentWorkReference: runtimeTransportAgentProfileWorkReferenceSchema.optional(),
		queuedWorkReferences: array(runtimeTransportAgentProfileWorkReferenceSchema).max(50).optional(),
		status: literal("working"),
		waitingCount: number().int().min(0).max(50)
	}),
	strictObject({
		currentWorkReference: runtimeTransportAgentProfileWorkReferenceSchema.optional(),
		queuedWorkReferences: array(runtimeTransportAgentProfileWorkReferenceSchema).max(50).optional(),
		recoveryAction: runtimeTransportAgentProfileWorkRecoveryActionSchema.optional(),
		recoveryTargetWakeLifecycleId: string().trim().min(1).max(512).optional(),
		status: literal("paused"),
		waitingCount: number().int().min(0).max(50)
	})
]);
const runtimeTransportAgentProfileOperationalContextsRequestSchema = strictObject({
	includeWorkReferences: boolean(),
	ownerUserId: string().trim().min(1).max(256),
	targetProfileIds: array(agentProfileIdSchema).min(1).max(100)
});
const runtimeTransportAgentProfileOperationalContextsResponseSchema = strictObject({ contexts: array(strictObject({
	operationalContext: runtimeTransportAgentProfileOperationalContextSchema,
	targetProfileId: agentProfileIdSchema
})).max(100) });
const runtimeTransportAgentProfileWorkRecoveryTargetSchema = strictObject({
	ownerUserId: string().trim().min(1).max(256),
	targetProfileId: agentProfileIdSchema
});
const MAX_RUNTIME_TRANSPORT_AGENT_PROFILE_WORK_USAGE_AVAILABLE_TARGETS = 100;
const runtimeTransportAgentProfileWorkUsageAvailableRequestSchema = strictObject({
	ownerUserId: string().trim().min(1).max(256),
	targetProfileIds: array(agentProfileIdSchema).min(1).max(100)
});
const runtimeTransportAgentProfileWorkUsageAvailableResponseSchema = discriminatedUnion("status", [strictObject({
	changedProfileIds: array(agentProfileIdSchema).max(100),
	continuationProfileIds: array(agentProfileIdSchema).max(100).default([]),
	status: literal("updated")
}), strictObject({
	changedProfileIds: tuple([]),
	continuationProfileIds: tuple([]).default([]),
	status: literal("unavailable")
})]);
const runtimeTransportAgentProfileWorkContinuationAuthorizeRequestSchema = runtimeTransportAgentProfileWorkRecoveryTargetSchema.extend({
	continuationClientMessageId: clientMessageIdSchema,
	currentAgentControllerRef: agentControllerRefSchema,
	currentBindingId: string().trim().min(1).max(512),
	currentConfigurationRevision: number().int().positive(),
	currentDefaultProjectId: string().trim().min(1).nullable().optional(),
	expectedInterruptedTargetWakeLifecycleId: string().trim().min(1).max(512),
	forkInterruptedConversation: boolean().optional(),
	requireAcceptedExecutionTargetRevision: literal(true)
});
const runtimeTransportAgentProfileWorkContinuationAuthorizeResponseSchema = strictObject({ status: enumFactory([
	"authorized",
	"needs_attention",
	"not_pending",
	"unavailable"
]) });
const runtimeTransportAgentProfileWorkContinuationNeedsAttentionRequestSchema = runtimeTransportAgentProfileWorkRecoveryTargetSchema.extend({ expectedInterruptedTargetWakeLifecycleId: string().trim().min(1).max(512) });
const runtimeTransportAgentProfileWorkContinuationNeedsAttentionResponseSchema = strictObject({ status: enumFactory([
	"marked",
	"not_pending",
	"unavailable"
]) });
const runtimeTransportAgentProfileWorkHandoffRequestSchema = runtimeTransportAgentProfileWorkRecoveryTargetSchema.extend({
	sourceDispatchId: string().trim().min(1).max(512),
	sourceTargetWakeLifecycleId: string().trim().min(1).max(512),
	successorDispatchId: string().trim().min(1).max(512),
	successorExecutionContextId: string().trim().min(1).max(512).optional(),
	successorTargetWakeLifecycleId: string().trim().min(1).max(512)
});
const runtimeTransportAgentProfileWorkHandoffResponseSchema = strictObject({ status: enumFactory([
	"already_handed_off",
	"handed_off",
	"source_not_occupied",
	"successor_conflict",
	"unavailable"
]) });
const runtimeTransportAgentProfileWorkQueueApproveRequestSchema = runtimeTransportAgentProfileWorkRecoveryTargetSchema;
const runtimeTransportAgentProfileWorkQueueApproveResponseSchema = strictObject({ status: enumFactory([
	"approved",
	"not_pending",
	"unavailable"
]) });
const runtimeTransportAgentProfileWorkSkipRequestSchema = runtimeTransportAgentProfileWorkRecoveryTargetSchema.extend({ expectedInterruptedTargetWakeLifecycleId: string().trim().min(1).max(512) });
const runtimeTransportAgentProfileWorkSkipResponseSchema = strictObject({ status: enumFactory([
	"skipped",
	"not_pending",
	"unavailable"
]) });
const runtimeTransportAgentProfileWorkLocalAttemptWithdrawalRequestSchema = runtimeTransportAgentProfileWorkRecoveryTargetSchema.extend({ expectedInterruptedTargetWakeLifecycleId: string().trim().min(1).max(512) });
const runtimeTransportAgentProfileWorkLocalAttemptWithdrawalResponseSchema = strictObject({ status: enumFactory([
	"not_pending",
	"not_started",
	"requested",
	"unavailable",
	"withdrawn"
]) });
const runtimeTransportAgentProfileExecutionTargetMutationRequestSchema = strictObject({
	operationId: string().trim().min(1).max(256),
	ownerUserId: string().trim().min(1).max(256),
	targetProfileId: agentProfileIdSchema
});
const runtimeTransportAgentProfileExecutionTargetMutationAcquireResponseSchema = discriminatedUnion("status", [
	strictObject({
		expiresAtMs: number().int().nonnegative(),
		operationId: string().trim().min(1).max(256),
		status: literal("acquired")
	}),
	strictObject({
		reasonCode: string().trim().min(1).max(256),
		status: literal("blocked")
	}),
	strictObject({
		reasonCode: string().trim().min(1).max(256),
		status: literal("unavailable")
	})
]);
const runtimeTransportAgentProfileExecutionTargetMutationReleaseResponseSchema = discriminatedUnion("status", [strictObject({ status: literal("released") }), strictObject({ status: literal("not_owner") })]);
const runtimeTransportErrorCodeSchema = enumFactory([
	"daemon.session.reject_conflict",
	"daemon.session.not_found",
	"daemon.session.connection_replaced",
	"daemon.session.stale_lease",
	"daemon.profile.offline",
	"daemon.profile.pending_first_report",
	"daemon.profile.configuration_not_synced",
	"daemon.runtime_contract.missing",
	"daemon.runtime_contract.mismatch",
	"daemon.delivery.timeout",
	"daemon.delivery.start_timeout",
	"daemon.delivery.execution_stalled",
	"daemon.delivery.finalization_timeout",
	"daemon.result.conflict",
	"daemon.result.identity_mismatch",
	"daemon.result.inbox_unavailable",
	"daemon.result.finalization_unavailable",
	"space.agent_reply.context_changed",
	"core.transport_restarting",
	"execution_token_authority_missing",
	"execution_token_expired",
	"execution_token_mismatch",
	"execution_token_missing",
	"execution_token_revoked",
	"execution_token_scope_mismatch",
	"execution_token_superseded",
	"execution_token_terminal_late",
	"execution_token_unknown",
	"runtime_coordinator.execution_id_missing",
	"runtime_coordinator.execution_id_mismatch",
	"runtime_coordinator.head_execution_link_unavailable",
	"runtime_coordinator.pre_agent_stop_requested",
	"runtime_coordinator.execution_token_authority_unavailable",
	"runtime_coordinator.execution_token_scope_mismatch",
	"runtime_coordinator.execution_token_unavailable",
	"runtime_coordinator.execution_context_mismatch",
	"runtime_coordinator.execution_context_unavailable"
]);
const runtimeTransportLeaseSchema = strictObject({
	profileId: string().min(1),
	leaseEpoch: number().int().positive()
});
const runtimeTransportBaseCapabilitiesSchema = strictObject({
	executionHost: executionHostObservationSchema.optional(),
	agentReplyContextReview: literal(true).optional(),
	informationProtectionPrompt: literal(true).optional(),
	workingLanguagePrompt: literal(true).optional(),
	localAgentInteraction: literal(true).optional(),
	profileConfigurationRevision: literal(true).optional(),
	localComponents: localComponentsStatusSchema.optional(),
	openMeldToolsControl: openMeldToolsControlCapabilitySchema.optional(),
	localAgentControl: strictObject({
		cancel: literal(true).optional(),
		interrupt: literal(true).optional(),
		manageInstallations: literal(true).optional(),
		manageInstallationsSafely: literal(true).optional(),
		forkSession: literal(true).optional(),
		refreshCatalog: literal(true).optional(),
		resumeControllerConversation: literal(true).optional(),
		setEnabled: literal(true).optional(),
		setProfileWorkspace: literal(true).optional()
	}).optional(),
	localProjectControl: strictObject({
		connectCandidate: literal(true).optional(),
		connectDeclaredPath: literal(true).optional(),
		discoverProjects: literal(true).optional(),
		readConnections: literal(true).optional(),
		moveConnection: literal(true).optional(),
		removeConnection: literal(true).optional(),
		removeMissingConnections: literal(true).optional(),
		setDefaultConnection: literal(true).optional()
	}).optional(),
	runtimeAdapters: array(daemonRuntimeAdapterCapabilitySnapshotSchema).min(1).optional()
});
const runtimeTransportCapabilitiesSchema = runtimeTransportBaseCapabilitiesSchema.extend({
	spaceWorkHandoff: literal(true).optional(),
	computerFileAccess: strictObject({
		readPolicy: literal(true).optional(),
		updatePolicy: literal(true).optional()
	}).optional(),
	localFolderPicker: localFolderPickerRuntimeCapabilitySchema.optional()
});
const localWakeRouteWorkingFolderObservationIdentityShape = {
	bindingId: string().min(1),
	configurationRevision: number().int().positive(),
	observedAtMs: number().int().nonnegative(),
	profileId: string().min(1)
};
const localProjectBindingGenerationSchema = string().trim().min(1);
const defaultProjectIdSchema = string().trim().min(1);
const localWakeRouteWorkingFolderReasonCodeSchema = enumFactory([
	"project.local_connection.missing",
	"project.local_connection.folder_missing",
	"project.local_connection.invalid",
	"profile.workspace.missing",
	"profile.workspace.invalid"
]);
const localWakeRouteWorkingFolderObservationSchema = union([
	strictObject({
		...localWakeRouteWorkingFolderObservationIdentityShape,
		defaultProjectId: defaultProjectIdSchema,
		localProjectBindingGeneration: localProjectBindingGenerationSchema,
		reasonCode: nullFactory(),
		source: literal("default_project"),
		status: literal("ready")
	}),
	strictObject({
		...localWakeRouteWorkingFolderObservationIdentityShape,
		defaultProjectId: defaultProjectIdSchema,
		localProjectBindingGeneration: nullFactory(),
		reasonCode: literal("project.local_connection.missing"),
		source: literal("default_project"),
		status: literal("missing")
	}),
	strictObject({
		...localWakeRouteWorkingFolderObservationIdentityShape,
		defaultProjectId: defaultProjectIdSchema,
		localProjectBindingGeneration: localProjectBindingGenerationSchema,
		reasonCode: literal("project.local_connection.folder_missing"),
		source: literal("default_project"),
		status: literal("missing")
	}),
	strictObject({
		...localWakeRouteWorkingFolderObservationIdentityShape,
		defaultProjectId: defaultProjectIdSchema,
		localProjectBindingGeneration: localProjectBindingGenerationSchema,
		reasonCode: literal("project.local_connection.invalid"),
		source: literal("default_project"),
		status: literal("invalid")
	}),
	strictObject({
		...localWakeRouteWorkingFolderObservationIdentityShape,
		defaultProjectId: nullFactory(),
		localProjectBindingGeneration: nullFactory(),
		reasonCode: nullFactory(),
		source: literal("profile_workspace"),
		status: literal("ready")
	}),
	strictObject({
		...localWakeRouteWorkingFolderObservationIdentityShape,
		defaultProjectId: nullFactory(),
		localProjectBindingGeneration: nullFactory(),
		reasonCode: literal("profile.workspace.missing"),
		source: literal("profile_workspace"),
		status: literal("missing")
	}),
	strictObject({
		...localWakeRouteWorkingFolderObservationIdentityShape,
		defaultProjectId: nullFactory(),
		localProjectBindingGeneration: nullFactory(),
		reasonCode: literal("profile.workspace.invalid"),
		source: literal("profile_workspace"),
		status: literal("invalid")
	})
]);
const localWakeRouteReportBaseShape = {
	reportedAtMs: number().int().nonnegative(),
	daemonSessionId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	serviceVersion: string().trim().min(1).nullable().optional(),
	includedProfileIds: array(string().min(1)).max(512),
	profileConfigurationRevisions: array(strictObject({
		bindingId: string().min(1),
		configurationRevision: number().int().positive(),
		profileId: string().min(1)
	})).max(512).optional(),
	skippedProfiles: array(skippedDaemonProfileSchema).max(512)
};
const localWakeRouteReportSchema = union([strictObject(localWakeRouteReportBaseShape), strictObject({
	...localWakeRouteReportBaseShape,
	expectedRoutesContractVersion: literal("v4"),
	workingFolderObservations: array(localWakeRouteWorkingFolderObservationSchema).max(512)
})]);
const runtimeTransportRejectedProfileRegistrationSchema = strictObject({
	profileId: string().min(1),
	reasonCode: runtimeTransportErrorCodeSchema,
	rejectedAtMs: number().int().nonnegative(),
	daemonSessionId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	serviceVersion: string().trim().min(1).nullable().optional()
});
const runtimeTransportRegisterSessionRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	daemonSessionId: string().min(1),
	serviceVersion: string().trim().min(1).optional(),
	connectionGeneration: number().int().positive(),
	profileIds: array(string().min(1)).max(512),
	localWakeRouteReport: localWakeRouteReportSchema.optional(),
	capabilities: runtimeTransportCapabilitiesSchema,
	runtimeContract: daemonRuntimeContractSchema.optional(),
	connectedAtMs: number().int().nonnegative()
});
const runtimeTransportRegisterSessionResponseSchema = strictObject({
	executionAdmissionConfirmation: literal(true).optional(),
	computerCare: literal(true).optional(),
	ok: boolean(),
	connectionGeneration: number().int().positive(),
	acceptedLeases: array(runtimeTransportLeaseSchema),
	rejectedProfiles: array(strictObject({
		profileId: string().min(1),
		reasonCode: runtimeTransportErrorCodeSchema
	}))
});
const runtimeTransportHeartbeatRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	daemonSessionId: string().min(1),
	acceptingDispatches: boolean().optional(),
	stopCause: runtimeTransportStopCauseSchema.optional(),
	serviceVersion: string().trim().min(1).optional(),
	leases: array(runtimeTransportLeaseSchema).max(512),
	capabilities: runtimeTransportCapabilitiesSchema.optional(),
	runtimeContract: daemonRuntimeContractSchema.optional(),
	sentAtMs: number().int().nonnegative()
});
const runtimeTransportHeartbeatResponseSchema = strictObject({
	ok: boolean(),
	serverNowMs: number().int().nonnegative(),
	staleLeases: array(runtimeTransportLeaseSchema),
	reasonCode: runtimeTransportErrorCodeSchema.optional()
});
const runtimeTransportRouteCatalogChangedReasonSchema = string().trim().min(1).max(100);
const runtimeTransportRouteCatalogChangedFramePayloadSchema = strictObject({ reason: runtimeTransportRouteCatalogChangedReasonSchema });
const runtimeTransportNotifyRouteCatalogChangedRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	reason: runtimeTransportRouteCatalogChangedReasonSchema,
	affectedProfileIds: array(string().trim().min(1)).max(200).optional()
});
const runtimeTransportNotifyRouteCatalogChangedResponseSchema = strictObject({
	ok: boolean(),
	notifiedSessions: number().int().nonnegative()
});
const runtimeTransportLookupOwnerServiceRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1)
});
const runtimeTransportLiveSessionSchema = strictObject({
	daemonSessionId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	serviceVersion: string().trim().min(1).nullable().optional(),
	connectionGeneration: number().int().positive(),
	lastHeartbeatMs: number().int().nonnegative(),
	capabilities: runtimeTransportCapabilitiesSchema,
	executionCompatibility: daemonExecutionCompatibilitySchema
});
const runtimeTransportLookupOwnerServiceResponseSchema = discriminatedUnion("online", [strictObject({
	online: literal(true),
	sessions: array(runtimeTransportLiveSessionSchema).min(1)
}), strictObject({
	online: literal(false),
	reasonCode: runtimeTransportErrorCodeSchema
})]);
const runtimeTransportSocketDisconnectClassSchema = enumFactory([
	"abnormal_socket_close",
	"normal_shutdown",
	"possible_deploy_or_edge_restart"
]);
const runtimeTransportSocketCloseReasonCodeSchema = enumFactory([
	"abnormal_closure",
	"going_away",
	"normal_closure",
	"other",
	"socket_error"
]);
const runtimeTransportSocketDisconnectRateBucketSchema = strictObject({
	hourStartMs: number().int().nonnegative(),
	disconnectClass: runtimeTransportSocketDisconnectClassSchema,
	deployAdjacentReason: string().trim().min(1).max(100).nullable(),
	count: number().int().positive(),
	firstObservedAtMs: number().int().nonnegative(),
	lastObservedAtMs: number().int().nonnegative()
});
const runtimeTransportSessionDisconnectDiagnosticSchema = strictObject({
	closeCode: number().int().nonnegative().max(4999),
	closeReasonCode: runtimeTransportSocketCloseReasonCodeSchema,
	connectedAtMs: number().int().nonnegative(),
	connectionGeneration: number().int().positive(),
	daemonSessionId: string().min(1).max(128),
	deployAdjacentReason: string().trim().min(1).max(100).nullable(),
	deviceId: string().min(1).max(128),
	disconnectedAtMs: number().int().nonnegative(),
	disconnectClass: runtimeTransportSocketDisconnectClassSchema,
	durationMs: number().int().nonnegative(),
	heartbeatAgeMs: number().int().nonnegative(),
	lane: daemonServiceLaneSchema,
	lastHeartbeatMs: number().int().nonnegative(),
	profileCount: number().int().nonnegative(),
	profileIds: array(string().min(1).max(128)).max(20),
	serviceVersion: string().trim().min(1).max(64).nullable(),
	wasClean: boolean()
});
const runtimeTransportDisconnectRatesResponseSchema = discriminatedUnion("ok", [strictObject({
	ok: literal(true),
	ownerUserId: string().min(1).nullable(),
	windowMs: number().int().nonnegative(),
	buckets: array(runtimeTransportSocketDisconnectRateBucketSchema),
	recentSessions: array(runtimeTransportSessionDisconnectDiagnosticSchema).max(20).optional()
}), strictObject({
	ok: literal(false),
	reasonCode: string().trim().min(1),
	buckets: array(runtimeTransportSocketDisconnectRateBucketSchema).length(0),
	recentSessions: array(runtimeTransportSessionDisconnectDiagnosticSchema).length(0).optional()
})]);
const runtimeTransportSetLocalAgentEnabledRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	agentId: string().trim().min(1).max(256),
	enabled: boolean()
});
const runtimeTransportSetLocalAgentEnabledResponseSchema = discriminatedUnion("status", [strictObject({
	status: literal("accepted"),
	payload: openMeldDaemonSetLocalAgentEnabledPayloadSchema
}), strictObject({
	status: literal("rejected"),
	reasonCode: string().min(1),
	message: string().min(1)
})]);
const runtimeTransportManageLocalAgentInstallationRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	request: openMeldDaemonManageLocalAgentInstallationRequestPayloadSchema
});
const runtimeTransportManageLocalAgentInstallationResponseSchema = discriminatedUnion("status", [strictObject({
	status: literal("accepted"),
	payload: openMeldDaemonManageLocalAgentInstallationPayloadSchema
}), strictObject({
	status: literal("rejected"),
	reasonCode: string().min(1),
	message: string().min(1)
})]);
const runtimeTransportOpenMeldToolsControlRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	requestId: string().min(1),
	request: openMeldToolsControlRequestSchema
});
const runtimeTransportOpenMeldToolsControlResponseSchema = discriminatedUnion("status", [strictObject({
	status: literal("accepted"),
	result: openMeldToolsControlResultSchema
}), strictObject({
	status: literal("rejected"),
	reasonCode: string().min(1),
	message: string().min(1)
})]);
const runtimeTransportRefreshLocalAgentCatalogRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	reason: string().trim().min(1).max(120)
});
const runtimeTransportRefreshLocalAgentCatalogResponseSchema = discriminatedUnion("status", [strictObject({
	status: literal("accepted"),
	payload: openMeldDaemonSyncRouteCatalogPayloadSchema
}), strictObject({
	status: literal("rejected"),
	reasonCode: string().min(1),
	message: string().min(1)
})]);
const runtimeTransportSetProfileWorkspaceRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	targetProfileId: agentProfileIdSchema,
	workspaceMode: profileWorkspaceModeSchema,
	workspacePath: profileWorkingDirectorySchema.optional()
}).superRefine((value, context) => {
	if (value.workspaceMode === "custom" && !value.workspacePath) context.addIssue({
		code: "custom",
		message: "workspacePath is required for a custom profile workspace",
		path: ["workspacePath"]
	});
	if (value.workspaceMode === "openmeld_managed" && value.workspacePath) context.addIssue({
		code: "custom",
		message: "workspacePath must be omitted for an OpenMeld-managed profile workspace",
		path: ["workspacePath"]
	});
});
const runtimeTransportSetProfileWorkspaceResponseSchema = discriminatedUnion("status", [strictObject({
	status: literal("accepted"),
	payload: openMeldDaemonSetProfileWorkspacePayloadSchema
}), strictObject({
	status: literal("rejected"),
	reasonCode: string().min(1),
	message: string().min(1)
})]);
const agentSessionForkOperationSchema = strictObject({
	agentControllerRef: agentControllerRefSchema,
	operationId: string().trim().min(1).max(256),
	organizationId: string().trim().min(1).max(256),
	profileName: string().trim().min(1).max(64),
	projectId: string().trim().min(1).max(256),
	sourceNativeSessionId: string().trim().min(1).max(512)
});
const runtimeTransportForkAgentSessionRequestSchema = agentSessionForkOperationSchema.extend({
	v: literal(1),
	ownerUserId: string().trim().min(1),
	deviceId: string().trim().min(1),
	lane: daemonServiceLaneSchema
});
const runtimeTransportForkAgentSessionResultSchema = discriminatedUnion("status", [
	agentSessionForkOperationSchema.extend({
		childNativeSessionId: string().trim().min(1).max(512),
		status: literal("completed")
	}),
	agentSessionForkOperationSchema.extend({
		message: string().trim().min(1).max(1e3),
		reasonCode: string().trim().min(1).max(256),
		status: literal("rejected")
	}),
	agentSessionForkOperationSchema.extend({
		message: string().trim().min(1).max(1e3),
		reasonCode: string().trim().min(1).max(256),
		status: literal("outcome_unknown")
	})
]);
const runtimeTransportLocalAgentControlForkSessionFramePayloadSchema = strictObject({
	requestId: string().min(1),
	request: agentSessionForkOperationSchema
});
const runtimeTransportLocalAgentControlSetEnabledFramePayloadSchema = strictObject({
	requestId: string().min(1),
	payload: openMeldDaemonSetLocalAgentEnabledRequestPayloadSchema
});
const runtimeTransportLocalAgentControlManageInstallationFramePayloadSchema = strictObject({
	requestId: string().min(1),
	payload: openMeldDaemonManageLocalAgentInstallationRequestPayloadSchema
});
const runtimeTransportLocalAgentControlRefreshCatalogFramePayloadSchema = strictObject({
	requestId: string().min(1),
	payload: openMeldDaemonSyncRouteCatalogRequestPayloadSchema
});
const runtimeTransportLocalAgentControlSetProfileWorkspaceFramePayloadSchema = strictObject({
	requestId: string().min(1),
	payload: openMeldDaemonSetProfileWorkspaceRequestPayloadSchema
});
const runtimeTransportLocalAgentControlCancelFramePayloadSchema = strictObject({
	requestId: string().min(1),
	payload: openMeldDaemonCancelRequestPayloadSchema
});
const runtimeTransportLocalAgentControlInterruptFramePayloadSchema = strictObject({
	requestId: string().min(1),
	payload: openMeldDaemonInterruptRequestPayloadSchema
});
const runtimeTransportLocalAgentControlResultFramePayloadSchema = strictObject({
	requestId: string().min(1),
	payload: union([
		openMeldDaemonManageLocalAgentInstallationPayloadSchema,
		openMeldDaemonSetLocalAgentEnabledPayloadSchema,
		openMeldDaemonSetProfileWorkspacePayloadSchema,
		openMeldDaemonSyncRouteCatalogPayloadSchema,
		openMeldDaemonCancelPayloadSchema,
		openMeldDaemonInterruptPayloadSchema,
		runtimeTransportForkAgentSessionResultSchema
	])
});
const runtimeTransportOpenMeldToolsControlFramePayloadSchema = strictObject({
	requestId: string().min(1),
	request: openMeldToolsControlRequestSchema
});
const runtimeTransportOpenMeldToolsControlResultFramePayloadSchema = strictObject({
	requestId: string().min(1),
	result: openMeldToolsControlResultSchema
});
const runtimeTransportInspectExecutionRecoveryRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().trim().min(1),
	identity: executionRecoveryIdentitySchema,
	destination: executionRecoveryDestinationSchema
});
const runtimeTransportInspectExecutionRecoveryResponseSchema = discriminatedUnion("status", [strictObject({
	status: literal("available"),
	exactAssociationVerified: literal(true),
	serviceSupportsResume: literal(true),
	identity: executionRecoveryIdentitySchema,
	reasonCode: literal("execution_recovery.exact_controller_conversation_available")
}), strictObject({
	status: literal("unavailable"),
	exactAssociationVerified: boolean(),
	identity: executionRecoveryIdentitySchema.nullable(),
	serviceSupportsResume: boolean(),
	reasonCode: string().trim().min(1)
})]);
const runtimeTransportResumeControllerConversationRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().trim().min(1),
	operationId: string().trim().min(1),
	identity: executionRecoveryIdentitySchema,
	destination: executionRecoveryDestinationSchema,
	providerRuntimeWorkingDirectory: string().trim().min(1).max(4096)
});
const runtimeTransportResumeControllerConversationFramePayloadSchema = strictObject({
	requestId: string().trim().min(1),
	operationId: string().trim().min(1),
	identity: executionRecoveryIdentitySchema,
	destination: executionRecoveryDestinationSchema,
	providerRuntimeWorkingDirectory: string().trim().min(1).max(4096),
	sourceEventId: string().trim().min(1),
	transportMode: runtimeTransportModeSchema
});
const runtimeTransportResumeControllerConversationResultSchema = discriminatedUnion("status", [strictObject({
	status: literal("completed"),
	operationId: string().trim().min(1),
	identity: executionRecoveryIdentitySchema,
	destination: executionRecoveryDestinationSchema,
	output: string().trim().min(1),
	agentControllerConversationId: string().trim().min(1)
}), strictObject({
	status: enumFactory(["outcome_unknown", "rejected"]),
	operationId: string().trim().min(1),
	identity: executionRecoveryIdentitySchema,
	destination: executionRecoveryDestinationSchema,
	reasonCode: string().trim().min(1),
	message: string().trim().min(1)
})]);
const runtimeTransportResumeControllerConversationResultFramePayloadSchema = strictObject({
	requestId: string().trim().min(1),
	result: runtimeTransportResumeControllerConversationResultSchema
});
const runtimeTransportResumeControllerConversationResponseSchema = runtimeTransportResumeControllerConversationResultSchema;
const runtimeTransportLookupRouteRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	profileId: string().min(1)
});
const runtimeTransportLookupRouteOwnerServiceEvidenceSchema = strictObject({
	daemonSessionId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	serviceVersion: string().trim().min(1).nullable().optional(),
	connectionGeneration: number().int().positive(),
	lastHeartbeatMs: number().int().nonnegative(),
	capabilities: runtimeTransportCapabilitiesSchema,
	executionCompatibility: daemonExecutionCompatibilitySchema
});
const runtimeTransportLookupRouteResponseSchema = discriminatedUnion("online", [strictObject({
	online: literal(true),
	acceptingDispatches: boolean().optional(),
	stopCause: runtimeTransportStopCauseSchema.optional(),
	daemonSessionId: string().min(1),
	deviceId: string().min(1),
	lane: daemonServiceLaneSchema,
	serviceVersion: string().trim().min(1).nullable().optional(),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	lastHeartbeatMs: number().int().nonnegative(),
	capabilities: runtimeTransportCapabilitiesSchema,
	executionCompatibility: daemonExecutionCompatibilitySchema,
	localWakeRouteReport: localWakeRouteReportSchema.optional()
}), strictObject({
	online: literal(false),
	reasonCode: runtimeTransportErrorCodeSchema,
	lastHeartbeatMs: number().int().nonnegative().optional(),
	ownerService: runtimeTransportLookupRouteOwnerServiceEvidenceSchema.optional(),
	executionCompatibility: daemonExecutionCompatibilitySchema.optional(),
	localWakeRouteReport: localWakeRouteReportSchema.optional(),
	rejectedProfileRegistration: runtimeTransportRejectedProfileRegistrationSchema.optional()
})]);
const runtimeTransportLookupRoutesBatchRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	profileIds: array(string().min(1)).min(1).max(100)
});
const runtimeTransportLookupRoutesBatchResponseSchema = strictObject({ routes: record(string(), runtimeTransportLookupRouteResponseSchema) });
const runtimeTransportDispatchSourceSignalSchema = strictObject({
	profileId: string().min(1).nullable(),
	profileKind: enumFactory([
		"agent",
		"human",
		"system"
	]).nullable(),
	profileHandle: profileHandleSchema.optional(),
	profileMentionLabel: spaceMentionLabelSchema.optional(),
	text: string().min(1).max(MAX_TEXT_LENGTH)
});
const runtimeTransportDeliverDispatchPayloadSchema = strictObject({
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId,
	executionToken: runtimeCoordinatorExecutionTokenEnvelopeSchema.optional(),
	attemptId: string().min(1),
	dispatchId: string().min(1),
	executionIdentity: runtimeTransportDeliveryExecutionIdentitySchema,
	taskId: string().min(1),
	spaceId: string().min(1),
	sourceEventId: string().min(1),
	sourceClientMessageId: string().min(1).max(200).nullable().optional(),
	sourceSignal: runtimeTransportDispatchSourceSignalSchema,
	targetProfileId: string().min(1),
	defaultProjectId: string().trim().min(1).optional(),
	organizationProtectedInformation: organizationProtectedInformationSchema.optional(),
	workingLanguage: workingLanguageGuidanceSchema.optional(),
	dispatchContractVersion: number().int().positive(),
	dispatchPolicy: runtimeDispatchPolicySchema,
	controllerSnapshot: openMeldDaemonDispatchControllerSnapshotSchema,
	contextIdMappingSpec: runtimeAgentControllerConversationIdMappingSpecSchema.nullable(),
	dispatchRulesSnapshot: openMeldDaemonDispatchRulesSnapshotSchema,
	spaceContractSnapshot: spaceContractSchema.optional(),
	continuitySnapshot: openMeldDaemonDispatchContinuitySnapshotSchema.optional(),
	enqueuedAtMs: number().int().nonnegative(),
	idempotencyKey: string().min(1),
	spacePassword: spacePasswordSchema.optional(),
	targetProfileName: string().min(1).max(64).optional(),
	targetProfileMentionLabel: spaceMentionLabelSchema.optional(),
	targetProfileIsCenterAgent: boolean().optional(),
	agentControllerConversationId: string().trim().min(1).optional(),
	executionContext: executionContextTaskSchema.optional(),
	deliveryRecoveryReason: openMeldDaemonDispatchDeliveryRecoveryReasonSchema.optional(),
	dispatchChainId: string().min(1).optional(),
	autoRelayDepth: number().int().nonnegative().optional(),
	visitedProfileIds: array(string().min(1)).max(256).optional(),
	visitedProfileIdsHash: string().min(1).optional(),
	transportMode: runtimeTransportModeSchema.nullable().optional(),
	streamingMode: runtimeStreamingModeSchema.optional(),
	sourceSignalSnapshot: openMeldDaemonDispatchSourceSignalSnapshotSchema.optional(),
	sourceMessageEnvelope: messageEnvelopeSchema.optional(),
	replyToSnapshot: openMeldDaemonDispatchReplyToSnapshotSchema.optional(),
	threadContext: spaceThreadContextSchema.optional(),
	spaceNameSnapshot: string().min(1).nullable().optional()
});
const runtimeTransportDeliverDispatchRequestSchema = strictObject({
	v: literal(1),
	dispatchId: string().min(1),
	attemptId: string().min(1),
	ownerUserId: string().min(1),
	profileId: string().min(1),
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	deliveryRecoveryReason: openMeldDaemonDispatchDeliveryRecoveryReasonSchema.optional(),
	payload: runtimeTransportDeliverDispatchPayloadSchema
});
const runtimeTransportDeliverDispatchResponseSchema = discriminatedUnion("status", [
	strictObject({
		accepted: literal(true),
		status: literal("delivered")
	}),
	strictObject({
		accepted: literal(true),
		status: literal("already_pending"),
		existingAttemptId: string().min(1)
	}),
	strictObject({
		accepted: literal(true),
		status: literal("already_completed"),
		completedAttemptId: string().min(1)
	}),
	strictObject({
		accepted: literal(false),
		status: literal("rejected"),
		reasonCode: runtimeTransportErrorCodeSchema
	})
]);
const runtimeTransportWaitDispatchResultRequestSchema = strictObject({
	dispatchId: string().min(1),
	attemptId: string().min(1),
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	timeoutMs: number().int().positive().optional()
});
const runtimeCoordinatorDecisionReasonCodeSchema = string().trim().min(1).max(160);
const runtimeTransportRuntimeCoordinatorDecisionEventKindSchema = enumFactory([
	"owner_conflict",
	"reclaim",
	"retry",
	"timeout"
]);
const runtimeTransportRuntimeCoordinatorDecisionTriggerSchema = enumFactory([
	"alarm",
	"daemon",
	"queue"
]);
const runtimeTransportRuntimeCoordinatorDecisionRequestSchema = strictObject({
	v: literal(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId,
	dispatchId: string().min(1),
	spaceId: string().min(1),
	targetProfileId: string().min(1),
	sourceClientMessageId: string().min(1).max(200).nullable(),
	attemptId: string().min(1).nullable().optional(),
	claimDecision: enumFactory([
		"continue_existing",
		"reset_existing",
		"claim_recovery",
		"claim_existing_inside_window",
		"reclaim_stale_dispatching",
		"reclaim_stale_queued"
	]).optional(),
	eventKind: runtimeTransportRuntimeCoordinatorDecisionEventKindSchema,
	idempotencyKey: string().min(1).max(512),
	observedRunId: string().min(1).nullable().optional(),
	reasonCode: runtimeCoordinatorDecisionReasonCodeSchema.optional(),
	trigger: runtimeTransportRuntimeCoordinatorDecisionTriggerSchema
});
const runtimeTransportRuntimeCoordinatorDecisionResponseSchema = discriminatedUnion("status", [strictObject({
	ok: literal(true),
	status: enumFactory(["already_recorded", "recorded"]),
	action: enumFactory([
		"diagnostic_only",
		"keep_waiting",
		"owner_conflict_terminal",
		"redeliver_current_run",
		"redelivery_deferred",
		"reclaim_blocked",
		"retry_blocked",
		"retry_run_issued",
		"terminal_failed",
		"timeout_terminal"
	]),
	reasonCode: runtimeCoordinatorDecisionReasonCodeSchema,
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId
}), strictObject({
	ok: literal(false),
	status: literal("retry_later"),
	reasonCode: runtimeCoordinatorDecisionReasonCodeSchema,
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId
})]);
const runtimeTransportRuntimeCoordinatorWakeStopRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId,
	dispatchId: string().min(1),
	spaceId: string().min(1),
	sourceClientMessageId: string().min(1).max(200).nullable(),
	targetProfileId: string().min(1),
	reason: string().trim().min(1).max(512).optional()
});
const runtimeTransportRuntimeCoordinatorWakeStopResponseSchema = postSpaceWakeStopResponseSchema;
const runtimeTransportDeletedAgentProfileWakeCancellationRequestSchema = strictObject({
	ownerUserId: string().trim().min(1).max(256),
	targetProfileId: agentProfileIdSchema
});
const runtimeTransportRuntimeCoordinatorHeadPreAgentStopReasonCodeSchema = enumFactory(["runtime_coordinator.pre_agent_stop_requested"]);
const runtimeTransportRuntimeCoordinatorHeadPreAgentStopRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	dispatchId: string().min(1),
	idempotencyKey: string().min(1).max(512),
	reason: string().trim().min(1).max(512).optional(),
	targetProfileMentionLabel: spaceMentionLabelSchema.optional(),
	targetProfileName: string().min(1).max(64).optional(),
	targetWakeLifecycleIdentity: targetWakeLifecycleIdentitySchema
});
const runtimeTransportRuntimeCoordinatorHeadPreAgentStopResponseSchema = discriminatedUnion("status", [strictObject({
	ok: literal(true),
	primaryDispatchId: string().min(1),
	publicationIntentId: string().min(1).max(4096),
	reasonCode: runtimeTransportRuntimeCoordinatorHeadPreAgentStopReasonCodeSchema,
	runtimeCoordinatorExecutionId: nullFactory(),
	status: enumFactory(["stopped_before_execution", "already_stopped"]),
	targetWakeLifecycleId: string().min(1).max(2048)
}), strictObject({
	ok: literal(false),
	primaryDispatchId: string().min(1).nullable(),
	publicationIntentId: string().min(1).max(4096).nullable(),
	reasonCode: string().min(1).max(160),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.nullable(),
	status: enumFactory([
		"conflict",
		"execution_linked",
		"retry_later"
	]),
	targetWakeLifecycleId: string().min(1).max(2048)
})]);
const runtimeTransportDeletedAgentProfileWakeCancellationResponseSchema = strictObject({
	executionStopOutcomes: array(runtimeTransportRuntimeCoordinatorWakeStopResponseSchema),
	headStopOutcomes: array(runtimeTransportRuntimeCoordinatorHeadPreAgentStopResponseSchema),
	status: enumFactory([
		"inactive",
		"cancellation_requested",
		"partially_requested",
		"unavailable"
	]),
	targetProfileId: agentProfileIdSchema
});
const runtimeTransportRuntimeCoordinatorHeadAcceptActionSchema = enumFactory([
	"create_execution",
	"skip_duplicate",
	"wait_in_queue"
]);
const runtimeTransportRuntimeCoordinatorHeadAcceptRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	dispatchId: string().min(1),
	idempotencyKey: string().min(1).max(512),
	acceptedDispatchSnapshot: openMeldDaemonAcceptedDispatchSnapshotSchema.optional(),
	targetProfileMentionLabel: spaceMentionLabelSchema.optional(),
	targetProfileName: string().min(1).max(64).optional(),
	targetWakeLifecycleIdentity: targetWakeLifecycleIdentitySchema
});
const runtimeTransportRuntimeCoordinatorHeadAcceptResponseSchema = discriminatedUnion("status", [
	strictObject({
		executionContext: executionContextTaskSchema.optional(),
		acceptedDispatchSnapshot: openMeldDaemonAcceptedDispatchSnapshotSchema.optional(),
		action: runtimeTransportRuntimeCoordinatorHeadAcceptActionSchema,
		ok: literal(true),
		primaryDispatchId: string().min(1),
		reasonCode: string().min(1).max(160),
		runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.nullable(),
		status: enumFactory([
			"accepted",
			"already_accepted",
			"execution_linked",
			"publication_intent_ready",
			"stopped_before_execution"
		]),
		targetWakeLifecycleId: string().min(1).max(2048)
	}),
	strictObject({
		acceptedDispatchSnapshot: openMeldDaemonAcceptedDispatchSnapshotSchema.optional(),
		action: literal("wait_in_queue"),
		ok: literal(true),
		primaryDispatchId: string().min(1),
		queuePosition: number().int().positive().max(50),
		reasonCode: literal("agent_profile_work_coordinator.queued"),
		runtimeCoordinatorExecutionId: nullFactory(),
		status: literal("queued"),
		targetWakeLifecycleId: string().min(1).max(2048),
		waitingCount: number().int().positive().max(50)
	}),
	strictObject({
		ok: literal(false),
		primaryDispatchId: string().min(1).nullable(),
		reasonCode: string().min(1).max(160),
		status: enumFactory(["conflict", "retry_later"]),
		targetWakeLifecycleId: string().min(1).max(2048)
	}),
	strictObject({
		ok: literal(false),
		primaryDispatchId: nullFactory(),
		reasonCode: literal("agent_profile_work_coordinator.queue_full"),
		status: literal("queue_full"),
		targetWakeLifecycleId: string().min(1).max(2048),
		waitingCount: literal(50)
	})
]);
const runtimeTransportRuntimeCoordinatorHeadLifecycleStatusSchema = enumFactory([
	"accepted",
	"execution_linked",
	"publication_intent_ready",
	"queued",
	"stopped_before_execution",
	"waiting"
]);
const runtimeTransportRuntimeCoordinatorHeadNoExecutionReasonCodeSchema = string().trim().min(1).max(160);
const runtimeTransportRuntimeCoordinatorHeadNoExecutionPublicationCategorySchema = enumFactory([
	"space_access_unavailable",
	"binding_required",
	"agent_profile_unavailable",
	"local_agent_unavailable",
	"local_connection_mismatch",
	"provider_access_unavailable",
	"provider_usage_unavailable",
	"service_not_connected",
	"update_required",
	"wake_route_registration_pending",
	"workspace_unavailable"
]);
const runtimeTransportRuntimeCoordinatorHeadNoExecutionRecoveryActionSchema = enumFactory([
	"send_new_wake",
	"set_up_agent_binding",
	"enable_agent_binding",
	"repair_agent_binding",
	"authorize_agent_binding_for_organization",
	"check_status",
	"connect_local_agent",
	"enable_local_agent",
	"reconnect_agent_binding",
	"review_agent_settings",
	"start_or_restart_openmeld_service",
	"update_openmeld_service",
	"wait_for_usage_report",
	"wait_for_usage_reset",
	"wait_for_wake_route_report"
]);
const runtimeTransportRuntimeCoordinatorHeadNoExecutionDecisionSourceSchema = enumFactory([
	"dispatch_preflight",
	"dispatch_delivery_pre_execution",
	"runtime_coordinator_head_waiting_deadline"
]);
const runtimeTransportRuntimeCoordinatorHeadNoExecutionRequestSchema = strictObject({
	v: literal(1),
	ownerUserId: string().min(1),
	dispatchId: string().min(1),
	idempotencyKey: string().min(1).max(512),
	decisionSource: runtimeTransportRuntimeCoordinatorHeadNoExecutionDecisionSourceSchema.optional(),
	reasonCode: runtimeTransportRuntimeCoordinatorHeadNoExecutionReasonCodeSchema,
	publicationCategory: runtimeTransportRuntimeCoordinatorHeadNoExecutionPublicationCategorySchema,
	userRecoveryAction: runtimeTransportRuntimeCoordinatorHeadNoExecutionRecoveryActionSchema,
	targetWakeLifecycleIdentity: targetWakeLifecycleIdentitySchema
});
const runtimeTransportRuntimeCoordinatorHeadNoExecutionResponseSchema = discriminatedUnion("status", [strictObject({
	ok: literal(true),
	primaryDispatchId: string().min(1),
	publicationIntentId: string().min(1).max(4096),
	reasonCode: runtimeTransportRuntimeCoordinatorHeadNoExecutionReasonCodeSchema,
	runtimeCoordinatorExecutionId: nullFactory(),
	status: enumFactory(["publication_intent_ready", "already_recorded"]),
	targetWakeLifecycleId: string().min(1).max(2048)
}), strictObject({
	ok: literal(false),
	primaryDispatchId: string().min(1).nullable(),
	publicationIntentId: string().min(1).max(4096).nullable(),
	reasonCode: string().min(1).max(160),
	runtimeCoordinatorExecutionId: nullFactory(),
	status: enumFactory(["conflict", "retry_later"]),
	targetWakeLifecycleId: string().min(1).max(2048)
})]);
const runtimeTransportRuntimeCoordinatorHeadWaitingReasonCodeSchema = enumFactory([
	"wake.device_wait.access_check_unavailable",
	"daemon.service.draining",
	"daemon.profile.offline",
	"daemon.profile.pending_first_report",
	"wake.device_wait.readiness_checking"
]);
const runtimeTransportRuntimeCoordinatorHeadWaitingCategorySchema = enumFactory([
	"space_access_unavailable",
	"service_not_connected",
	"wake_route_registration_pending",
	"local_agent_unavailable"
]);
const runtimeTransportRuntimeCoordinatorHeadWaitingRecoveryActionSchema = enumFactory([
	"start_or_restart_openmeld_service",
	"wait_for_wake_route_report",
	"check_status"
]);
const runtimeCoordinatorOfflineWaitingDecisionShape = {
	reasonCode: literal("daemon.profile.offline"),
	userRecoveryAction: literal("start_or_restart_openmeld_service"),
	waitingCategory: literal("service_not_connected")
};
const runtimeCoordinatorPendingFirstReportWaitingDecisionShape = {
	reasonCode: literal("daemon.profile.pending_first_report"),
	userRecoveryAction: literal("wait_for_wake_route_report"),
	waitingCategory: literal("wake_route_registration_pending")
};
const runtimeCoordinatorReadinessWaitingDecisionShape = {
	reasonCode: enumFactory(["wake.device_wait.readiness_checking", "daemon.service.draining"]),
	userRecoveryAction: literal("check_status"),
	waitingCategory: literal("local_agent_unavailable")
};
const runtimeCoordinatorAccessWaitingDecisionShape = {
	reasonCode: literal("wake.device_wait.access_check_unavailable"),
	userRecoveryAction: literal("check_status"),
	waitingCategory: literal("space_access_unavailable")
};
const runtimeCoordinatorHeadWaitingBoundaryCommonShape = {
	deadlineAtMs: number().int().nonnegative(),
	deadlineOutcome: literal("publication_intent_ready"),
	observedAtMs: number().int().nonnegative(),
	softDeadlineAtMs: number().int().nonnegative(),
	triggerOwner: literal("runtime_coordinator"),
	waitingSinceMs: number().int().nonnegative()
};
const runtimeTransportRuntimeCoordinatorHeadWaitingBoundarySchema = discriminatedUnion("reasonCode", [
	strictObject({
		...runtimeCoordinatorHeadWaitingBoundaryCommonShape,
		...runtimeCoordinatorAccessWaitingDecisionShape,
		trigger: literal("space_access_rechecked"),
		userVisibleState: literal("waiting_for_space_access"),
		waitingFor: literal("space_access_check")
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingBoundaryCommonShape,
		...runtimeCoordinatorReadinessWaitingDecisionShape,
		trigger: literal("agent_readiness_reported"),
		userVisibleState: literal("waiting_for_agent_readiness"),
		waitingFor: literal("agent_readiness_report")
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingBoundaryCommonShape,
		...runtimeCoordinatorOfflineWaitingDecisionShape,
		trigger: literal("openmeld_service_route_connected"),
		userVisibleState: literal("waiting_for_openmeld_service"),
		waitingFor: literal("openmeld_service_route_connection")
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingBoundaryCommonShape,
		...runtimeCoordinatorPendingFirstReportWaitingDecisionShape,
		trigger: literal("wake_route_reported"),
		userVisibleState: literal("waiting_for_wake_route_report"),
		waitingFor: literal("wake_route_report")
	})
]);
const runtimeCoordinatorHeadWaitingRequestCommonShape = {
	v: literal(1),
	ownerUserId: string().min(1),
	dispatchId: string().min(1),
	idempotencyKey: string().min(1).max(512),
	observedAtMs: number().int().nonnegative(),
	targetWakeLifecycleIdentity: targetWakeLifecycleIdentitySchema
};
const runtimeTransportRuntimeCoordinatorHeadWaitingRequestSchema = discriminatedUnion("reasonCode", [
	strictObject({
		...runtimeCoordinatorHeadWaitingRequestCommonShape,
		...runtimeCoordinatorAccessWaitingDecisionShape
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingRequestCommonShape,
		...runtimeCoordinatorReadinessWaitingDecisionShape
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingRequestCommonShape,
		...runtimeCoordinatorOfflineWaitingDecisionShape
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingRequestCommonShape,
		...runtimeCoordinatorPendingFirstReportWaitingDecisionShape
	})
]);
const runtimeCoordinatorHeadWaitingResponseCommonShape = {
	deadlineAtMs: number().int().nonnegative(),
	nextTriggerAtMs: number().int().nonnegative(),
	ok: literal(true),
	primaryDispatchId: string().min(1),
	runtimeCoordinatorExecutionId: nullFactory(),
	softDeadlineAtMs: number().int().nonnegative(),
	status: literal("waiting"),
	targetWakeLifecycleId: string().min(1).max(2048)
};
const runtimeTransportRuntimeCoordinatorHeadWaitingResponseSchema = union([
	strictObject({
		...runtimeCoordinatorHeadWaitingResponseCommonShape,
		...runtimeCoordinatorAccessWaitingDecisionShape
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingResponseCommonShape,
		...runtimeCoordinatorReadinessWaitingDecisionShape
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingResponseCommonShape,
		...runtimeCoordinatorOfflineWaitingDecisionShape
	}),
	strictObject({
		...runtimeCoordinatorHeadWaitingResponseCommonShape,
		...runtimeCoordinatorPendingFirstReportWaitingDecisionShape
	}),
	strictObject({
		ok: literal(true),
		primaryDispatchId: string().min(1),
		publicationIntentId: string().min(1).max(4096),
		reasonCode: runtimeTransportRuntimeCoordinatorHeadNoExecutionReasonCodeSchema,
		runtimeCoordinatorExecutionId: nullFactory(),
		status: literal("publication_intent_ready"),
		targetWakeLifecycleId: string().min(1).max(2048)
	}),
	strictObject({
		ok: literal(false),
		primaryDispatchId: string().min(1).nullable(),
		reasonCode: string().min(1).max(160),
		runtimeCoordinatorExecutionId: nullFactory(),
		status: enumFactory(["conflict", "retry_later"]),
		targetWakeLifecycleId: string().min(1).max(2048)
	})
]);
const runtimeTransportRuntimeCoordinatorHeadNoExecutionDecisionTraceSchema = strictObject({
	decidedAtMs: int().min(0),
	publicationCategory: runtimeTransportRuntimeCoordinatorHeadNoExecutionPublicationCategorySchema,
	reasonCode: runtimeTransportRuntimeCoordinatorHeadNoExecutionReasonCodeSchema,
	source: runtimeTransportRuntimeCoordinatorHeadNoExecutionDecisionSourceSchema,
	userRecoveryAction: runtimeTransportRuntimeCoordinatorHeadNoExecutionRecoveryActionSchema
});
const runtimeTransportRuntimeCoordinatorHeadPublicationIntentTraceSchema = union([strictObject({
	intentId: string().min(1).max(4096),
	kind: literal("no_execution_failure"),
	publicationCategory: runtimeTransportRuntimeCoordinatorHeadNoExecutionPublicationCategorySchema,
	readyAtMs: int().min(0),
	reasonCode: runtimeTransportRuntimeCoordinatorHeadNoExecutionReasonCodeSchema,
	status: literal("ready_for_phase_6"),
	userRecoveryAction: runtimeTransportRuntimeCoordinatorHeadNoExecutionRecoveryActionSchema
}), strictObject({
	intentId: string().min(1).max(4096),
	kind: literal("stopped_notice"),
	readyAtMs: int().min(0),
	reasonCode: runtimeTransportRuntimeCoordinatorHeadPreAgentStopReasonCodeSchema,
	status: literal("ready_for_phase_6")
})]);
const runtimeTransportRuntimeCoordinatorHeadPublicationOutcomeTraceSchema = strictObject({
	failurePublicationCommandId: string().min(1).max(4096),
	finalMessageKind: enumFactory([
		"failure_notice",
		"status",
		"success_reply",
		"unknown"
	]).nullable(),
	observedAtMs: int().min(0),
	publicationErrorCode: string().min(1).max(160).nullable(),
	publishedSignalId: string().min(1).nullable(),
	status: enumFactory([
		"failure_published",
		"already_final",
		"failure_publication_failed"
	])
});
const runtimeTransportRuntimeCoordinatorHeadPublicationExhaustionTraceSchema = strictObject({
	attemptCount: int().positive(),
	exhaustedAtMs: int().min(0),
	lastFailureAtMs: int().min(0),
	lastReasonCode: string().min(1).max(160),
	reasonCode: literal("runtime_coordinator.publication_retry_exhausted"),
	status: literal("publication_exhausted")
});
const runtimeTransportRuntimeCoordinatorHeadTargetDisplayTraceSchema = strictObject({
	targetProfileMentionLabel: spaceMentionLabelSchema.nullable(),
	targetProfileName: string().min(1).max(64).nullable()
});
const runtimeTransportRuntimeCoordinatorHeadLifecycleTraceRecordSchema = strictObject({
	createdAtMs: int().min(0),
	duplicateDispatchCount: int().min(0),
	executionLinkedAtMs: int().min(0).nullable(),
	modelVersion: literal("openmeld.runtime_coordinator.head_lifecycle.v1"),
	noExecutionDecision: union([runtimeTransportRuntimeCoordinatorHeadNoExecutionDecisionTraceSchema, nullFactory()]),
	ownerUserId: string().min(1),
	primaryDispatchId: string().min(1),
	publicationIntent: union([runtimeTransportRuntimeCoordinatorHeadPublicationIntentTraceSchema, nullFactory()]),
	publicationExhaustion: runtimeTransportRuntimeCoordinatorHeadPublicationExhaustionTraceSchema.optional(),
	publicationOutcome: runtimeTransportRuntimeCoordinatorHeadPublicationOutcomeTraceSchema.optional(),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.nullable(),
	status: runtimeTransportRuntimeCoordinatorHeadLifecycleStatusSchema,
	targetWakeLifecycleIdentity: targetWakeLifecycleIdentitySchema,
	targetWakeLifecycleId: string().min(1).max(2048),
	targetDisplay: runtimeTransportRuntimeCoordinatorHeadTargetDisplayTraceSchema.optional(),
	updatedAtMs: int().min(0),
	waitingBoundary: union([runtimeTransportRuntimeCoordinatorHeadWaitingBoundarySchema, nullFactory()])
});
const runtimeTransportRuntimeCoordinatorHeadLifecycleTraceRequestSchema = strictObject({
	v: literal(1),
	targetWakeLifecycleIdentity: targetWakeLifecycleIdentitySchema
});
const runtimeTransportRuntimeCoordinatorHeadLifecycleTraceResponseSchema = discriminatedUnion("readStatus", [
	strictObject({
		lifecycle: runtimeTransportRuntimeCoordinatorHeadLifecycleTraceRecordSchema,
		ok: literal(true),
		readStatus: literal("found")
	}),
	strictObject({
		ok: literal(true),
		readStatus: literal("missing"),
		reasonCode: literal("runtime_coordinator.head_lifecycle_not_found"),
		targetWakeLifecycleId: string().min(1).max(2048)
	}),
	strictObject({
		ok: literal(false),
		readStatus: literal("authority_unavailable"),
		reasonCode: string().min(1).max(160),
		targetWakeLifecycleId: string().min(1).max(2048)
	})
]);
const runtimeTransportPreExecutionHandoffReasonCodeSchema = enumFactory(["daemon.profile.offline"]);
const runtimeTransportPreExecutionHandoffRequestSchema = strictObject({
	v: literal(1),
	attemptId: string().min(1).nullable().optional(),
	autoRelayDepth: number().int().nonnegative().optional(),
	dispatchChainId: string().min(1).nullable().optional(),
	dispatchId: string().min(1),
	firstSeenAtMs: number().int().nonnegative(),
	idempotencyKey: string().min(1).max(512),
	observedAtMs: number().int().nonnegative(),
	reasonCode: runtimeTransportPreExecutionHandoffReasonCodeSchema,
	sourceClientMessageId: string().min(1).max(200).nullable().optional(),
	sourceEventId: string().min(1),
	spaceId: string().min(1),
	spacePassword: spacePasswordSchema.optional(),
	targetProfileId: string().min(1),
	targetProfileMentionLabel: spaceMentionLabelSchema.optional(),
	targetProfileName: string().min(1).max(64).optional(),
	taskId: string().min(1),
	threadContext: spaceThreadContextSchema.optional(),
	visitedProfileIds: array(string().min(1)).max(256).optional(),
	visitedProfileIdsHash: string().min(1).nullable().optional()
});
const runtimeTransportPreExecutionHandoffResponseSchema = discriminatedUnion("status", [
	strictObject({
		deadlineAtMs: number().int().nonnegative(),
		nextAlarmAtMs: number().int().nonnegative(),
		ok: literal(true),
		reasonCode: runtimeTransportPreExecutionHandoffReasonCodeSchema,
		status: literal("waiting_for_recovery")
	}),
	strictObject({
		deadlineAtMs: number().int().nonnegative(),
		ok: literal(true),
		publishedSignalId: string().min(1),
		reasonCode: runtimeTransportPreExecutionHandoffReasonCodeSchema,
		resultRef: string().min(1),
		status: literal("failure_published")
	}),
	strictObject({
		deadlineAtMs: number().int().nonnegative(),
		nextPublicationRetryAtMs: number().int().nonnegative(),
		ok: literal(true),
		publicationErrorCode: string().min(1),
		reasonCode: runtimeTransportPreExecutionHandoffReasonCodeSchema,
		resultRef: string().min(1),
		status: literal("failure_publication_retry_scheduled")
	}),
	strictObject({
		deadlineAtMs: number().int().nonnegative(),
		ok: literal(true),
		reasonCode: runtimeTransportPreExecutionHandoffReasonCodeSchema,
		status: literal("handoff_recovered")
	}),
	strictObject({
		deadlineAtMs: number().int().nonnegative(),
		ok: literal(true),
		reasonCode: runtimeTransportPreExecutionHandoffReasonCodeSchema,
		status: literal("legacy_evidence_only")
	}),
	strictObject({
		ok: literal(false),
		reasonCode: string().min(1),
		status: literal("retry_later")
	})
]);
const runtimeTransportDispatchLifecyclePhaseSchema = enumFactory([
	"accepted_local",
	"running",
	"completed_local"
]);
const runtimeTransportAgentControllerConversationOccupancySchema = strictObject({
	agentControllerConversationId: string().trim().min(1),
	contractVersion: literal(1),
	state: enumFactory(["available", "occupied"])
});
const runtimeTransportDispatchLocalQueueWaitReasonSchema = enumFactory([
	"workspace_conflict",
	"agent_profile_concurrency",
	"agent_controller_maintenance",
	"global_concurrency",
	"local_execution_lane_concurrency",
	"none"
]);
const runtimeTransportDispatchLocalQueueDiagnosticsSchema = strictObject({
	activeForLane: number().int().nonnegative(),
	activeGlobal: number().int().nonnegative(),
	globalConcurrency: number().int().positive(),
	queueDepth: number().int().nonnegative(),
	queueMax: number().int().positive(),
	queuePosition: union([number().int().positive(), nullFactory()]),
	status: enumFactory(["queued", "starting_runtime"]),
	waitReason: runtimeTransportDispatchLocalQueueWaitReasonSchema
});
const runtimeTransportDispatchLifecycleRequestSchema = strictObject({
	acknowledgementId: string().uuid().optional(),
	v: literal(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.optional(),
	executionTokenEcho: runtimeCoordinatorExecutionTokenEchoSchema.optional(),
	dispatchId: string().min(1),
	attemptId: string().min(1),
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	phase: runtimeTransportDispatchLifecyclePhaseSchema,
	lifecycleAtMs: number().int().nonnegative(),
	agentControllerConversationOccupancy: runtimeTransportAgentControllerConversationOccupancySchema.optional(),
	localQueue: runtimeTransportDispatchLocalQueueDiagnosticsSchema.optional()
});
const runtimeTransportDispatchLifecycleAcknowledgementSchema = strictObject({
	acknowledgementId: string().uuid(),
	dispatchId: string().min(1),
	attemptId: string().min(1),
	connectionGeneration: number().int().positive(),
	accepted: boolean(),
	validForMs: number().int().nonnegative(),
	reasonCode: string().min(1).nullable()
});
const runtimeTransportDispatchRuntimeEvidencePhaseSchema = enumFactory([
	"received",
	"claimed",
	"runtime_started",
	"first_output",
	"completed",
	"failed"
]);
const runtimeTransportDispatchRuntimeEvidenceRequestSchema = strictObject({
	v: literal(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.optional(),
	executionTokenEcho: runtimeCoordinatorExecutionTokenEchoSchema.optional(),
	dispatchId: string().min(1),
	attemptId: string().min(1),
	executionIdentityEcho: runtimeTransportRuntimeEvidenceIdentityEchoSchema,
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	phase: runtimeTransportDispatchRuntimeEvidencePhaseSchema,
	evidenceAtMs: number().int().nonnegative(),
	taskId: string().min(1),
	spaceId: string().min(1),
	sourceSignalId: string().min(1),
	sourceClientMessageId: string().min(1).max(200).nullable().optional(),
	targetProfileId: string().min(1),
	claimLeaseId: string().min(1).optional(),
	durationMs: number().finite().nonnegative().optional(),
	reasonCode: string().trim().min(1).max(160).optional(),
	errorName: string().trim().min(1).max(160).optional()
});
const LOCAL_AGENT_TOOL_ACTIVITY_NAMES = [
	"agent_run_command",
	"agent_read_files",
	"agent_edit_files",
	"agent_search_files",
	"agent_search_web",
	"agent_open_webpage",
	"agent_delegate_task",
	"agent_work_with_image",
	"agent_use_tool"
];
const localAgentToolActivityNameSchema = enumFactory(LOCAL_AGENT_TOOL_ACTIVITY_NAMES);
const localAgentStreamToolPartSchema = agentStreamToolPartSchema.extend({ toolName: localAgentToolActivityNameSchema });
const runtimeTransportDispatchToolActivityRequestSchema = strictObject({
	v: literal(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.optional(),
	executionTokenEcho: runtimeCoordinatorExecutionTokenEchoSchema.optional(),
	dispatchId: string().min(1),
	attemptId: string().min(1),
	executionIdentityEcho: runtimeTransportRuntimeEvidenceIdentityEchoSchema,
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	sequence: number().int().positive(),
	observedAtMs: number().int().nonnegative(),
	taskId: string().min(1),
	spaceId: string().min(1),
	sourceSignalId: string().min(1),
	targetProfileId: string().min(1),
	part: localAgentStreamToolPartSchema
});
const runtimeTransportDispatchReasoningRequestSchema = runtimeTransportDispatchToolActivityRequestSchema.omit({ part: true }).extend({
	thinkingSnapshot: string().max(LOCAL_AGENT_REASONING_MAX_CHARS),
	reasoningState: localAgentReasoningStateSchema
});
const runtimeTransportDispatchInteractionRequestSchema = strictObject({
	v: literal(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId,
	executionTokenEcho: runtimeCoordinatorExecutionTokenEchoSchema,
	dispatchId: string().min(1),
	attemptId: string().min(1),
	executionIdentityEcho: runtimeTransportRuntimeEvidenceIdentityEchoSchema,
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	taskId: string().min(1),
	interaction: agentInteractionRequestSchema
});
const runtimeTransportDispatchInteractionResponseFramePayloadSchema = strictObject({
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId,
	dispatchId: string().min(1),
	attemptId: string().min(1),
	interactionId: string().trim().min(1).max(256),
	response: agentInteractionResponseSchema,
	respondedAtMs: number().int().nonnegative()
});
const runtimeTransportDispatchInteractionDeliveredRequestSchema = strictObject({
	v: literal(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId,
	executionTokenEcho: runtimeCoordinatorExecutionTokenEchoSchema,
	dispatchId: string().min(1),
	attemptId: string().min(1),
	executionIdentityEcho: runtimeTransportRuntimeEvidenceIdentityEchoSchema,
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	interactionId: string().trim().min(1).max(256),
	deliveredAtMs: number().int().nonnegative()
});
const runtimeTransportRuntimeCoordinatorReadInteractionRequestSchema = strictObject({
	v: literal(1),
	actorOwnerUserId: string().min(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId
});
const runtimeTransportRuntimeCoordinatorReadInteractionResponseSchema = strictObject({ pendingInteraction: agentInteractionPendingSummarySchema.nullable() });
const runtimeTransportRuntimeCoordinatorRespondInteractionRequestSchema = strictObject({
	v: literal(1),
	actorOwnerUserId: string().min(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId,
	interactionId: string().trim().min(1).max(256),
	response: agentInteractionResponseSchema
});
const runtimeTransportRuntimeCoordinatorRespondInteractionResponseSchema = discriminatedUnion("status", [strictObject({
	ok: literal(true),
	status: enumFactory(["accepted", "already_resolved"]),
	respondedAtMs: number().int().nonnegative()
}), strictObject({
	ok: literal(false),
	status: enumFactory([
		"expired",
		"interaction_mismatch",
		"not_found",
		"owner_mismatch",
		"response_invalid",
		"retry_later"
	]),
	message: string().trim().min(1).max(1e3)
})]);
const nullableLifecyclePhaseTimestampSchema = union([number().int().nonnegative(), nullFactory()]);
const runtimeTransportDispatchLifecycleSnapshotSchema = strictObject({
	phase: enumFactory([
		"starting",
		"accepted_local",
		"running",
		"completed_local",
		"result_received"
	]),
	createdAtMs: number().int().nonnegative(),
	acceptedLocalAtMs: nullableLifecyclePhaseTimestampSchema,
	runningAtMs: nullableLifecyclePhaseTimestampSchema,
	completedLocalAtMs: nullableLifecyclePhaseTimestampSchema,
	lastLifecycleAtMs: number().int().nonnegative()
});
const runtimeTransportWaitDispatchResultResponseSchema = discriminatedUnion("state", [
	strictObject({
		state: literal("completed"),
		lifecycle: runtimeTransportDispatchLifecycleSnapshotSchema.optional(),
		taskResult: openMeldDaemonTaskResultEnvelopeSchema
	}),
	strictObject({
		state: literal("pending"),
		lifecycle: runtimeTransportDispatchLifecycleSnapshotSchema
	}),
	strictObject({
		state: literal("failed"),
		lifecycle: runtimeTransportDispatchLifecycleSnapshotSchema.optional(),
		reasonCode: runtimeTransportErrorCodeSchema
	})
]);
const runtimeTransportSubmitTaskResultRequestSchema = strictObject({
	v: literal(1),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.optional(),
	executionTokenEcho: runtimeCoordinatorExecutionTokenEchoSchema.optional(),
	dispatchId: string().min(1),
	attemptId: string().min(1).optional(),
	executionIdentityEcho: runtimeTransportResultIdentityEchoSchema,
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	taskResult: openMeldDaemonTaskResultEnvelopeSchema
});
const runtimeTransportSubmitTaskResultResponseSchema = discriminatedUnion("status", [
	strictObject({
		ok: literal(true),
		status: literal("accepted")
	}),
	strictObject({
		ok: literal(false),
		status: literal("review_required"),
		reasonCode: literal("space.agent_reply.context_changed")
	}),
	strictObject({
		ok: literal(false),
		status: literal("retry_later"),
		reasonCode: runtimeTransportErrorCodeSchema
	}),
	strictObject({
		ok: literal(false),
		status: literal("rejected_terminal"),
		reasonCode: runtimeTransportErrorCodeSchema
	})
]);
const runtimeTransportDispatchPreviewRequestSchema = strictObject({
	v: literal(1),
	dispatchId: string().min(1),
	attemptId: string().min(1),
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	previewText: string().min(1).max(MAX_TEXT_LENGTH)
});
const runtimeTransportDispatchPreviewEndRequestSchema = strictObject({
	v: literal(1),
	dispatchId: string().min(1),
	attemptId: string().min(1),
	daemonSessionId: string().min(1),
	connectionGeneration: number().int().positive(),
	leaseEpoch: number().int().positive(),
	targetProfileName: string().min(1).max(64).optional()
});
const daemonStreamCodeSchema = enumFactory([
	"daemon.computer_file_access.read_policy",
	"daemon.computer_file_access.read_policy_result",
	"daemon.computer_file_access.update_policy",
	"daemon.computer_file_access.update_policy_result",
	"daemon.local_folder_picker.select_folders",
	"daemon.local_folder_picker.select_folders_result",
	"daemon.session.register",
	"daemon.session.heartbeat",
	"daemon.dispatch.deliver",
	"daemon.dispatch.lifecycle",
	"daemon.dispatch.lifecycle_acknowledged",
	"daemon.dispatch.runtime_evidence",
	"daemon.dispatch.tool_activity",
	"daemon.dispatch.reasoning",
	"daemon.reasoning.preferences",
	"daemon.dispatch.interaction_request",
	"daemon.dispatch.interaction_response",
	"daemon.dispatch.interaction_delivered",
	"daemon.dispatch.preview",
	"daemon.dispatch.preview_end",
	"daemon.dispatch.accepted",
	"daemon.dispatch.result",
	"daemon.route_catalog.changed",
	"daemon.local_agent_control.cancel",
	"daemon.local_agent_control.interrupt",
	"daemon.local_agent_control.fork_session",
	"daemon.local_agent_control.manage_installation",
	"daemon.local_agent_control.refresh_catalog",
	"daemon.local_agent_control.set_enabled",
	"daemon.local_agent_control.set_profile_workspace",
	"daemon.local_agent_control.result",
	"daemon.openmeld_tools_control.request",
	"daemon.openmeld_tools_control.result",
	"daemon.execution_recovery.resume_controller_conversation",
	"daemon.execution_recovery.resume_controller_conversation_result",
	"daemon.local_project_control.discover_projects",
	"daemon.local_project_control.discover_projects_result",
	"daemon.local_project_control.connect_candidate",
	"daemon.local_project_control.connect_candidate_result",
	"daemon.local_project_control.connect_declared_path",
	"daemon.local_project_control.connect_declared_path_result",
	"daemon.local_project_control.set_default_connection",
	"daemon.local_project_control.set_default_connection_result",
	"daemon.local_project_control.remove_connection",
	"daemon.local_project_control.remove_connection_result",
	"daemon.local_project_control.remove_missing_connections",
	"daemon.local_project_control.remove_missing_connections_result",
	"daemon.error"
]);
const daemonStreamFrameSchema = strictObject({
	v: literal(1),
	code: daemonStreamCodeSchema,
	payload: record(string(), unknown())
});
const daemonStreamErrorFrameCodeSchema = runtimeTransportErrorCodeSchema.or(union([literal("schema.invalid"), literal("schema.invalid.runtime_response")]));
const daemonStreamErrorFrameSchema = strictObject({
	v: literal(1),
	code: literal("daemon.error"),
	payload: strictObject({
		code: daemonStreamErrorFrameCodeSchema,
		message: string().min(1)
	})
});
//#endregion
export { CURRENT_DAEMON_EXECUTION_CONTRACT_EPOCH, LOCAL_AGENT_TOOL_ACTIVITY_NAMES, MAX_RUNTIME_TRANSPORT_AGENT_PROFILE_WORK_USAGE_AVAILABLE_TARGETS, OPENMELD_EXECUTION_ADMISSION_HEADER, RUNTIME_TRANSPORT_SCHEMA_VERSION, daemonExecutionCompatibilitySchema, daemonRuntimeContractReasonCodeSchema, daemonRuntimeContractSchema, daemonStreamCodeSchema, daemonStreamErrorFrameCodeSchema, daemonStreamErrorFrameSchema, daemonStreamFrameSchema, localAgentToolActivityNameSchema, localWakeRouteReportSchema, localWakeRouteWorkingFolderObservationSchema, localWakeRouteWorkingFolderReasonCodeSchema, runtimeTransportAgentControllerConversationOccupancySchema, runtimeTransportAgentProfileExecutionStateRequestSchema, runtimeTransportAgentProfileExecutionStateResponseSchema, runtimeTransportAgentProfileExecutionTargetMutationAcquireResponseSchema, runtimeTransportAgentProfileExecutionTargetMutationReleaseResponseSchema, runtimeTransportAgentProfileExecutionTargetMutationRequestSchema, runtimeTransportAgentProfileOperationalContextSchema, runtimeTransportAgentProfileOperationalContextsRequestSchema, runtimeTransportAgentProfileOperationalContextsResponseSchema, runtimeTransportAgentProfileWorkContinuationAuthorizeRequestSchema, runtimeTransportAgentProfileWorkContinuationAuthorizeResponseSchema, runtimeTransportAgentProfileWorkContinuationNeedsAttentionRequestSchema, runtimeTransportAgentProfileWorkContinuationNeedsAttentionResponseSchema, runtimeTransportAgentProfileWorkHandoffRequestSchema, runtimeTransportAgentProfileWorkHandoffResponseSchema, runtimeTransportAgentProfileWorkLocalAttemptWithdrawalRequestSchema, runtimeTransportAgentProfileWorkLocalAttemptWithdrawalResponseSchema, runtimeTransportAgentProfileWorkQueueApproveRequestSchema, runtimeTransportAgentProfileWorkQueueApproveResponseSchema, runtimeTransportAgentProfileWorkRecoveryActionSchema, runtimeTransportAgentProfileWorkRecoveryReadRequestSchema, runtimeTransportAgentProfileWorkRecoveryReadResponseSchema, runtimeTransportAgentProfileWorkSkipRequestSchema, runtimeTransportAgentProfileWorkSkipResponseSchema, runtimeTransportAgentProfileWorkUsageAvailableRequestSchema, runtimeTransportAgentProfileWorkUsageAvailableResponseSchema, runtimeTransportBaseCapabilitiesSchema, runtimeTransportCapabilitiesSchema, runtimeTransportDeletedAgentProfileWakeCancellationRequestSchema, runtimeTransportDeletedAgentProfileWakeCancellationResponseSchema, runtimeTransportDeliverDispatchPayloadSchema, runtimeTransportDeliverDispatchRequestSchema, runtimeTransportDeliverDispatchResponseSchema, runtimeTransportDisconnectRatesResponseSchema, runtimeTransportDispatchInteractionDeliveredRequestSchema, runtimeTransportDispatchInteractionRequestSchema, runtimeTransportDispatchInteractionResponseFramePayloadSchema, runtimeTransportDispatchLifecycleAcknowledgementSchema, runtimeTransportDispatchLifecyclePhaseSchema, runtimeTransportDispatchLifecycleRequestSchema, runtimeTransportDispatchLifecycleSnapshotSchema, runtimeTransportDispatchLocalQueueDiagnosticsSchema, runtimeTransportDispatchLocalQueueWaitReasonSchema, runtimeTransportDispatchPreviewEndRequestSchema, runtimeTransportDispatchPreviewRequestSchema, runtimeTransportDispatchReasoningRequestSchema, runtimeTransportDispatchRuntimeEvidencePhaseSchema, runtimeTransportDispatchRuntimeEvidenceRequestSchema, runtimeTransportDispatchSourceSignalSchema, runtimeTransportDispatchToolActivityRequestSchema, runtimeTransportErrorCodeSchema, runtimeTransportForkAgentSessionRequestSchema, runtimeTransportForkAgentSessionResultSchema, runtimeTransportHeartbeatRequestSchema, runtimeTransportHeartbeatResponseSchema, runtimeTransportInspectExecutionRecoveryRequestSchema, runtimeTransportInspectExecutionRecoveryResponseSchema, runtimeTransportLeaseSchema, runtimeTransportLocalAgentControlCancelFramePayloadSchema, runtimeTransportLocalAgentControlForkSessionFramePayloadSchema, runtimeTransportLocalAgentControlInterruptFramePayloadSchema, runtimeTransportLocalAgentControlManageInstallationFramePayloadSchema, runtimeTransportLocalAgentControlRefreshCatalogFramePayloadSchema, runtimeTransportLocalAgentControlResultFramePayloadSchema, runtimeTransportLocalAgentControlSetEnabledFramePayloadSchema, runtimeTransportLocalAgentControlSetProfileWorkspaceFramePayloadSchema, runtimeTransportLookupOwnerServiceRequestSchema, runtimeTransportLookupOwnerServiceResponseSchema, runtimeTransportLookupRouteOwnerServiceEvidenceSchema, runtimeTransportLookupRouteRequestSchema, runtimeTransportLookupRouteResponseSchema, runtimeTransportLookupRoutesBatchRequestSchema, runtimeTransportLookupRoutesBatchResponseSchema, runtimeTransportManageLocalAgentInstallationRequestSchema, runtimeTransportManageLocalAgentInstallationResponseSchema, runtimeTransportNotifyRouteCatalogChangedRequestSchema, runtimeTransportNotifyRouteCatalogChangedResponseSchema, runtimeTransportOpenMeldToolsControlFramePayloadSchema, runtimeTransportOpenMeldToolsControlRequestSchema, runtimeTransportOpenMeldToolsControlResponseSchema, runtimeTransportOpenMeldToolsControlResultFramePayloadSchema, runtimeTransportPreExecutionHandoffReasonCodeSchema, runtimeTransportPreExecutionHandoffRequestSchema, runtimeTransportPreExecutionHandoffResponseSchema, runtimeTransportRefreshLocalAgentCatalogRequestSchema, runtimeTransportRefreshLocalAgentCatalogResponseSchema, runtimeTransportRegisterSessionRequestSchema, runtimeTransportRegisterSessionResponseSchema, runtimeTransportRejectedProfileRegistrationSchema, runtimeTransportResumeControllerConversationFramePayloadSchema, runtimeTransportResumeControllerConversationRequestSchema, runtimeTransportResumeControllerConversationResponseSchema, runtimeTransportResumeControllerConversationResultFramePayloadSchema, runtimeTransportResumeControllerConversationResultSchema, runtimeTransportRouteCatalogChangedFramePayloadSchema, runtimeTransportRouteCatalogChangedReasonSchema, runtimeTransportRuntimeCoordinatorDecisionEventKindSchema, runtimeTransportRuntimeCoordinatorDecisionRequestSchema, runtimeTransportRuntimeCoordinatorDecisionResponseSchema, runtimeTransportRuntimeCoordinatorDecisionTriggerSchema, runtimeTransportRuntimeCoordinatorHeadAcceptActionSchema, runtimeTransportRuntimeCoordinatorHeadAcceptRequestSchema, runtimeTransportRuntimeCoordinatorHeadAcceptResponseSchema, runtimeTransportRuntimeCoordinatorHeadLifecycleStatusSchema, runtimeTransportRuntimeCoordinatorHeadLifecycleTraceRecordSchema, runtimeTransportRuntimeCoordinatorHeadLifecycleTraceRequestSchema, runtimeTransportRuntimeCoordinatorHeadLifecycleTraceResponseSchema, runtimeTransportRuntimeCoordinatorHeadNoExecutionDecisionSourceSchema, runtimeTransportRuntimeCoordinatorHeadNoExecutionPublicationCategorySchema, runtimeTransportRuntimeCoordinatorHeadNoExecutionReasonCodeSchema, runtimeTransportRuntimeCoordinatorHeadNoExecutionRecoveryActionSchema, runtimeTransportRuntimeCoordinatorHeadNoExecutionRequestSchema, runtimeTransportRuntimeCoordinatorHeadNoExecutionResponseSchema, runtimeTransportRuntimeCoordinatorHeadPreAgentStopReasonCodeSchema, runtimeTransportRuntimeCoordinatorHeadPreAgentStopRequestSchema, runtimeTransportRuntimeCoordinatorHeadPreAgentStopResponseSchema, runtimeTransportRuntimeCoordinatorHeadWaitingCategorySchema, runtimeTransportRuntimeCoordinatorHeadWaitingReasonCodeSchema, runtimeTransportRuntimeCoordinatorHeadWaitingRecoveryActionSchema, runtimeTransportRuntimeCoordinatorHeadWaitingRequestSchema, runtimeTransportRuntimeCoordinatorHeadWaitingResponseSchema, runtimeTransportRuntimeCoordinatorReadInteractionRequestSchema, runtimeTransportRuntimeCoordinatorReadInteractionResponseSchema, runtimeTransportRuntimeCoordinatorRespondInteractionRequestSchema, runtimeTransportRuntimeCoordinatorRespondInteractionResponseSchema, runtimeTransportRuntimeCoordinatorWakeStopRequestSchema, runtimeTransportRuntimeCoordinatorWakeStopResponseSchema, runtimeTransportSessionDisconnectDiagnosticSchema, runtimeTransportSetLocalAgentEnabledRequestSchema, runtimeTransportSetLocalAgentEnabledResponseSchema, runtimeTransportSetProfileWorkspaceRequestSchema, runtimeTransportSetProfileWorkspaceResponseSchema, runtimeTransportSocketCloseReasonCodeSchema, runtimeTransportSocketDisconnectClassSchema, runtimeTransportSocketDisconnectRateBucketSchema, runtimeTransportSubmitTaskResultRequestSchema, runtimeTransportSubmitTaskResultResponseSchema, runtimeTransportWaitDispatchResultRequestSchema, runtimeTransportWaitDispatchResultResponseSchema };

//# sourceMappingURL=runtime-transport.js.map